import { useEffect, useState } from "react";
import { fetchEmployeeYggHistory } from "../lib/api";
import { Award, TrendingUp, AlertTriangle, Loader2 } from "lucide-react";

const CODE_META = {
  terfi_edebilir:      { label: "Terfi Edebilir",         icon: TrendingUp,     dot: "ring-emerald-400", iconColor: "text-emerald-600", chip: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  mevcut_rolde_devam:  { label: "Mevcut Rolde Devam",     icon: Award,          dot: "ring-sky-400",     iconColor: "text-sky-600",     chip: "bg-sky-100 text-sky-800 border-sky-200" },
  alaninin_uzmani:     { label: "Alanının Uzmanı",        icon: Award,          dot: "ring-indigo-400",  iconColor: "text-indigo-600",  chip: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  anahtar_calisan:     { label: "Anahtar Çalışan",        icon: AlertTriangle,  dot: "ring-amber-400",   iconColor: "text-amber-600",   chip: "bg-amber-100 text-amber-800 border-amber-200" },
  tespit_icin_erken:   { label: "Tespit İçin Erken",      icon: Award,          dot: "ring-slate-400",   iconColor: "text-slate-500",   chip: "bg-slate-100 text-slate-700 border-slate-200" },
};
const PYS_COLOR = { A: "text-emerald-700 bg-emerald-50", B: "text-sky-700 bg-sky-50", C: "text-amber-700 bg-amber-50", D: "text-rose-700 bg-rose-50" };

/**
 * YggHistoryTimeline — Multi-year YGG evaluation timeline.
 * Only displays COMPLETED cycles (calibration.talent_code present) so pending cycles don't pollute the history.
 */
export default function YggHistoryTimeline({ employeeId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchEmployeeYggHistory(employeeId)
      .then((d) => {
        const currentYear = new Date().getFullYear();
        // History = only PAST completed calibrations (exclude current year and future test cycles)
        const completed = (d.items || []).filter((e) =>
          (e.calibration?.talent_code) &&
          (e.stage_status?.calibration === "completed") &&
          (e.cycle_year || 0) < currentYear
        );
        completed.sort((a, b) => (b.cycle_year || 0) - (a.cycle_year || 0));
        setItems(completed);
      })
      .finally(() => setLoading(false));
  }, [employeeId]);

  if (loading) return <div className="card-surface p-6 text-sm text-slate-400" data-testid="ygg-history-loading"><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Geçmiş yükleniyor…</div>;
  if (items.length === 0) return (
    <div className="card-surface p-6" data-testid="ygg-history-empty">
      <div className="label-eyebrow mb-2">YGG Geçmişi</div>
      <p className="text-sm text-slate-500">Henüz tamamlanmış YGG değerlendirmesi yok. Aktif döngü kalibre edildikten sonra burada geçmiş dönemlerin görünecek.</p>
    </div>
  );

  return (
    <div className="card-surface p-6" data-testid="ygg-history">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="label-eyebrow">Yetenek Gözden Geçirme Geçmişi</div>
          <h3 className="font-display text-xl font-semibold">Yıl-Yıl Değerlendirmelerim</h3>
          <p className="text-xs text-slate-500 mt-1">Toplam <span className="font-semibold text-slate-700">{items.length}</span> kalibre edilmiş dönem.</p>
        </div>
      </div>

      <div className="relative pl-6">
        <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-gradient-to-b from-emerald-400 via-sky-400 to-slate-200" />
        <div className="space-y-4">
          {items.map((e) => {
            const cal = e.calibration || {};
            const meta = CODE_META[cal.talent_code] || CODE_META.mevcut_rolde_devam;
            const Icon = meta.icon;
            const pys = cal.pys_letter_post || (e.manager_eval || {}).pys_letter_pre || "-";
            const mgr = e.manager_eval || {};
            return (
              <div key={e.id} className="relative" data-testid={`ygg-year-${e.cycle_year}`}>
                <div className={`absolute -left-6 w-4 h-4 rounded-full border-2 border-white ring-2 ${meta.dot} bg-white flex items-center justify-center`}>
                  <Icon className={`w-2.5 h-2.5 ${meta.iconColor}`} />
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <div className="flex items-center flex-wrap gap-2 mb-2">
                    <span className="text-2xl font-display font-bold text-slate-800">{e.cycle_year}</span>
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${meta.chip}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    {pys !== "-" && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold ${PYS_COLOR[pys] || "text-slate-700 bg-slate-100"}`}>
                        PYS · {pys}
                      </span>
                    )}
                    {cal.talent_horizon && <span className="text-xs text-slate-500">· {cal.talent_horizon}</span>}
                    {mgr.mobility && <span className="text-xs text-slate-500">· Mobilite: <span className="font-medium">{mgr.mobility}</span></span>}
                    {mgr.risk_status && <span className={`text-xs px-1.5 py-0.5 rounded ${mgr.risk_status === "yüksek" ? "bg-rose-100 text-rose-700" : mgr.risk_status === "orta" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>Risk: {mgr.risk_status}</span>}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700 mb-0.5">Güçlü Yönler</div>
                      <div className="text-slate-700">{mgr.strengths || (e.survey?.top3_strengths || []).join(", ") || "-"}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-amber-700 mb-0.5">Gelişim Alanları</div>
                      <div className="text-slate-700">{mgr.dev_areas || (e.survey?.top3_dev_areas || []).join(", ") || "-"}</div>
                    </div>
                  </div>
                  {(cal.calibration_note || mgr.notes) && (
                    <div className="mt-3 pt-3 border-t border-slate-200 text-xs italic text-slate-600">
                      &ldquo;{cal.calibration_note || mgr.notes}&rdquo;
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
